import React from 'react';
import { Helmet } from 'react-helmet';

import siteConfig from '@config/website.config';
import {
  generateJSONLDSchema,
  generateJSONLDSchemaFAQ,
  generateJSONLDSchemaArticle,
  generateJSONLDSchemaHowTo,
} from '@utils/SEOUtils';

// eslint-disable-next-line complexity
function SEO({
  t,
  seo,
  children,
  slug = '/',
  lang,
  thumbnail,
  FAQSchemaData,
  BlogData,
  HowToSchema,
}) {
  let canonicalURL = seo.CanonicalURL;

  if (canonicalURL && !canonicalURL.endsWith('/')) {
    canonicalURL = `${canonicalURL}/`;
  }

  const ogImage = thumbnail;

  const url = `https://invideo.io${slug}`;

  const LDschema = generateJSONLDSchema(seo, url, lang, 'WebPage');

  const FAQSchema = generateJSONLDSchemaFAQ(FAQSchemaData);

  const ArticleSchema =
    BlogData && generateJSONLDSchemaArticle(BlogData, url, lang);
  const HowTo =
    HowToSchema && generateJSONLDSchemaHowTo(HowToSchema, url, lang);

  return (
    <Helmet>
      <title>{seo.Title || siteConfig.siteTitle}</title>
      <meta
        name="description"
        content={seo.Description || siteConfig.siteDescription}
      />
      <meta name="keywords" content={seo.Keywords || siteConfig.keywords} />

      <meta property="og:locale" content={lang === 'en' ? 'en_US' : lang} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={seo.Title || siteConfig.siteTitle} />
      <meta
        property="og:description"
        content={seo.Description || siteConfig.siteDescription}
      />
      <meta property="og:url" content={url} />
      <meta
        property="og:site_name"
        content="InVideo - Online Video Creator for Content and Marketing Videos"
      />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta
        property="article:publisher"
        content="https://www.facebook.com/invideo.io/"
      />
      <meta property="fb:admins" content="198297767789959" />
      <meta property="fb:app_id" content="1907729596109046" />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:site"
        content="InVideo - Online Video Creator for Content and Marketing Videos"
      />
      <meta
        name="twitter:description"
        content={seo.Description || siteConfig.siteDescription}
      />
      <meta name="twitter:title" content={seo.Title || siteConfig.siteTitle} />

      {canonicalURL && <link rel="canonical" href={canonicalURL} />}

      {(seo.RobotFollow || seo.RobotIndex) && (
        <meta
          name="robots"
          content={`${seo.RobotFollow || ''}${
            seo.RobotFollow && seo.RobotIndex ? ', ' : ''
          }${seo.RobotIndex || ''}`}
        />
      )}

      {FAQSchema && (
        <script type="application/ld+json">{JSON.stringify(FAQSchema)}</script>
      )}

      {ArticleSchema ? (
        <script type="application/ld+json">
          {JSON.stringify(ArticleSchema)}
        </script>
      ) : (
        <script type="application/ld+json">{JSON.stringify(LDschema)}</script>
      )}
      {HowTo &&
        HowTo.map(data => {
          return (
            <script key={data.name} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          );
        })}

      {Array.isArray(seo.Meta) &&
        seo.Meta.map(meta => (
          <meta key={meta.id} name={meta.Name} content={meta.Content} />
        ))}

      {Array.isArray(seo.Hreflang) &&
        seo.Hreflang.map(hreflink => (
          <link
            key={hreflink.Lang}
            rel="alternate"
            hrefLang={hreflink.Lang}
            href={hreflink.Link}
          />
        ))}

      {children}
    </Helmet>
  );
}

export default SEO;
